import { CommonModule, DatePipe, DecimalPipe } from '@angular/common';
import {
  Component,
  DestroyRef,
  OnInit,
  computed,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { Observable } from 'rxjs';

import { ConfirmModalComponent } from '../../../shared/components/ui/confirm-modal/confirm-modal.component';
import { DataStateComponent } from '../../../shared/components/ui/data-state/data-state.component';
import { ModalComponent } from '../../../shared/components/ui/modal/modal.component';
import { QrScannerComponent } from '../../../shared/components/ui/qr-scanner/qr-scanner.component';
import { PageHeaderComponent } from '../../../shared/components/ui/page-header/page-header.component';
import { UserCellComponent } from '../../../shared/components/ui/user-cell/user-cell.component';
import { toErrorMessage } from '../../../shared/helpers/http-error';
import { ChatsService } from '../../../shared/services/chats.service';
import { InvoicesService } from '../../../shared/services/invoices.service';
import { RentalsService } from '../../../shared/services/rentals.service';
import { InvoiceStatus } from '../../../shared/types/invoice.types';
import {
  CancellationRefund,
  RENTAL_STATUS_META,
  Rental,
} from '../../../shared/types/rental.types';
import { TPipe } from '../../../shared/pipe/t.pipe';
import { LocalizedDatePipe } from '../../../shared/pipe/localized-date.pipe';
import { LocaleService } from '../../../shared/services/locale.service';
import { serverDate } from '../../../shared/helpers/dates';

@Component({
  selector: 'app-rental-detail',
  imports: [CommonModule, RouterModule, ReactiveFormsModule, DatePipe, DecimalPipe, PageHeaderComponent, DataStateComponent, ConfirmModalComponent, ModalComponent, UserCellComponent, TPipe, LocalizedDatePipe, QrScannerComponent],
  templateUrl: './rental-detail.component.html',
})
export class RentalDetailComponent implements OnInit {
  private readonly locale = inject(LocaleService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly rentalsService = inject(RentalsService);
  private readonly invoicesService = inject(InvoicesService);
  private readonly chatsService = inject(ChatsService);
  private readonly destroyRef = inject(DestroyRef);

  readonly rental = signal<Rental | null>(null);
  readonly loading = signal(true);
  readonly loadError = signal<string | null>(null);

  /** Companion invoice — looked up by rentalId once the rental is loaded. */
  readonly invoiceId = signal<number | null>(null);
  readonly invoiceStatus = signal<InvoiceStatus | null>(null);
  readonly invoiceLoading = signal(false);
  /** True once we've attempted the lookup (regardless of outcome). */
  readonly invoiceFetched = signal(false);

  // delete modal
  readonly deleteOpen = signal(false);
  readonly deleting = signal(false);
  readonly deleteError = signal<string | null>(null);

  // ── Transitions de cycle de vie (accept/reject/start/finish/cancel) ─────
  readonly txOpen = signal(false);
  readonly txBusy = signal(false);
  readonly txError = signal<string | null>(null);
  readonly txTitle = signal('');
  readonly txMessage = signal('');
  readonly txConfirmLabel = signal('');
  readonly txBusyLabel = signal('');
  readonly txDanger = signal(false);
  private txRun: (() => Observable<unknown>) | null = null;
  /** Callback optionnel exécuté avec le résultat de la transition (ex. annulation
   *  → afficher le remboursement) avant le refetch. */
  private txOnSuccess: ((result: unknown) => void) | null = null;

  // ── Résultat d'annulation (remboursement calculé par le back) ───────────
  readonly refundOpen = signal(false);
  readonly refundResult = signal<CancellationRefund | null>(null);

  // ── Preview du remboursement (affiché DANS le modal de confirmation) ────
  readonly previewLoading = signal(false);
  readonly preview = signal<CancellationRefund | null>(null);
  readonly previewError = signal<string | null>(null);

  // ── Confirmation paiement espèces ───────────────────────────────────────
  readonly cashOpen = signal(false);
  readonly cashBusy = signal(false);
  readonly cashError = signal<string | null>(null);
  readonly cashRef = new FormControl('', { nonNullable: true });

  readonly statusMeta = RENTAL_STATUS_META;

  // ── Remise / restitution par scan du QR du client + photos « après » ────
  /** Scanner ouvert : 'start' (QR de retrait, CONFIRMED) ou 'finish' (QR de restitution, STARTED). */
  readonly scanOpen = signal<'start' | 'finish' | null>(null);
  readonly scanBusy = signal(false);
  readonly scanError = signal<string | null>(null);
  readonly uploadingAfter = signal(false);
  readonly afterError = signal<string | null>(null);
  private readonly scanner = viewChild(QrScannerComponent);
  /** Photos « après » déjà envoyées : condition du back pour terminer (409 sinon). */
  readonly afterCount = computed(() => this.rental()?.photosAfter?.length ?? 0);
  readonly beforeCount = computed(() => this.rental()?.photosBefore?.length ?? 0);

  openScan(kind: 'start' | 'finish'): void {
    this.scanError.set(null);
    this.scanOpen.set(kind);
  }

  closeScan(): void {
    this.scanOpen.set(null);
    this.scanBusy.set(false);
  }

  /** Le code scanné est envoyé tel quel au back, qui vérifie jeton, rôle, statut et photos. */
  onScanned(payload: string): void {
    const kind = this.scanOpen();
    if (!kind || this.scanBusy()) return;
    this.scanBusy.set(true);
    this.scanError.set(null);
    const call = kind === 'start' ? this.rentalsService.startByQr(payload) : this.rentalsService.finishByQr(payload);
    call.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (r) => { this.rental.set(r); this.scanBusy.set(false); this.scanOpen.set(null); this.fetchInvoiceLookup(); },
      error: (err: unknown) => { this.scanBusy.set(false); this.scanError.set(toErrorMessage(err, 'rentals.scan.failed')); this.scanner()?.reset(); },
    });
  }

  /** Photos d'état des lieux de restitution (loueur) — avant le scan de fin. */
  uploadAfter(e: Event): void {
    const input = e.target as HTMLInputElement;
    const files = Array.from(input.files ?? []);
    if (!files.length) return;
    this.uploadingAfter.set(true);
    this.afterError.set(null);
    this.rentalsService.uploadAfterPhotos(this.id, files).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => { this.uploadingAfter.set(false); input.value = ''; this.fetch(); },
      error: (err: unknown) => { this.uploadingAfter.set(false); input.value = ''; this.afterError.set(toErrorMessage(err, 'rentals.scan.uploadFailed')); },
    });
  }

  // ── Visibilité des actions (owner, selon le statut) ─────────────────────
  readonly status = computed(() => this.rental()?.rentalStatusEnum ?? null);
  readonly canAccept = computed(() => this.status() === 'PENDING');
  readonly canReject = computed(() => this.status() === 'PENDING');
  readonly canStart = computed(() => this.status() === 'CONFIRMED');
  readonly canFinish = computed(() => this.status() === 'STARTED');
  readonly canCancel = computed(() => this.status() === 'CONFIRMED');
  /** Encaissement espèces : facture existante non payée sur une location active. */
  readonly canConfirmCash = computed(
    () =>
      this.invoiceId() != null &&
      this.invoiceStatus() !== 'PAID' &&
      (['CONFIRMED', 'STARTED', 'FINISHED'] as const).includes(
        (this.status() ?? '') as 'CONFIRMED' | 'STARTED' | 'FINISHED',
      ),
  );
  /** Le chat existe à partir de CONFIRMED (créé à l'acceptation). */
  readonly canChat = computed(() =>
    (['CONFIRMED', 'STARTED', 'FINISHED'] as const).includes(
      (this.status() ?? '') as 'CONFIRMED' | 'STARTED' | 'FINISHED',
    ),
  );
  readonly openingChat = signal(false);
  readonly chatError = signal<string | null>(null);

  /** Ouvre la conversation liée à cette location (résolue par rental.id). */
  openChat(): void {
    if (this.openingChat()) return;
    this.openingChat.set(true);
    this.chatError.set(null);
    this.chatsService
      .forRental(this.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (chat) => {
          this.openingChat.set(false);
          if (chat) {
            void this.router.navigate(['/my-chats', chat.id]);
          } else {
            this.chatError.set("Aucune conversation pour cette location pour l'instant.");
          }
        },
        error: (err: unknown) => {
          this.openingChat.set(false);
          this.chatError.set(toErrorMessage(err, "Impossible d'ouvrir la conversation."));
        },
      });
  }

  /** true s'il existe au moins une action disponible (pour afficher la barre). */
  readonly hasActions = computed(
    () =>
      this.canAccept() ||
      this.canReject() ||
      this.canStart() ||
      this.canFinish() ||
      this.canCancel() ||
      this.canConfirmCash(),
  );

  readonly vehicleName = computed(() => {
    const v = this.rental()?.vehicle;
    if (!v) return '—';
    const make = v.make?.entityCode ?? v.make?.name ?? '';
    const model = v.model?.entityCode ?? v.model?.name ?? '';
    return [make, model].filter(Boolean).join(' ') || this.locale.t('label.vehicleNum', { id: v.id });
  });

  /** Computed duration in days from startDate → endDate. */
  readonly durationDays = computed(() => {
    const r = this.rental();
    if (!r?.startDate || !r?.endDate) return null;
    const start = serverDate(r.startDate).getTime();
    const end = serverDate(r.endDate).getTime();
    if (Number.isNaN(start) || Number.isNaN(end)) return null;
    const ms = end - start;
    return Math.max(1, Math.round(ms / (1000 * 60 * 60 * 24)));
  });

  /** True if the rental was returned later than the planned end date. */
  readonly returnedLate = computed(() => {
    const r = this.rental();
    if (!r?.actualReturnDate || !r?.endDate) return false;
    return serverDate(r.actualReturnDate).getTime() > serverDate(r.endDate).getTime();
  });

  private id!: number;

  ngOnInit(): void {
    const idParam = this.route.snapshot.paramMap.get('id');
    const parsed = idParam ? Number(idParam) : NaN;
    if (!Number.isFinite(parsed)) {
      this.loadError.set('Identifiant invalide.');
      this.loading.set(false);
      return;
    }
    this.id = parsed;
    this.fetch();
  }

  fetch(): void {
    this.loading.set(true);
    this.loadError.set(null);
    this.rentalsService
      .getMine(this.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (r) => {
          this.rental.set(r);
          this.loading.set(false);
          this.fetchInvoiceLookup();
        },
        error: (err: unknown) => {
          this.loading.set(false);
          this.loadError.set(
            toErrorMessage(err, 'errors.rentalLoadFailed'),
          );
        },
      });
  }

  /** Best-effort companion invoice lookup. A 404 just means "no invoice yet". */
  private fetchInvoiceLookup(): void {
    this.invoiceLoading.set(true);
    this.invoiceFetched.set(false);
    this.invoicesService
      .getByRental(this.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (inv) => {
          this.invoiceId.set(inv.id);
          this.invoiceStatus.set(inv.status ?? null);
          this.invoiceLoading.set(false);
          this.invoiceFetched.set(true);
        },
        error: () => {
          this.invoiceId.set(null);
          this.invoiceStatus.set(null);
          this.invoiceLoading.set(false);
          this.invoiceFetched.set(true);
        },
      });
  }

  // ── Transitions de cycle de vie ────────────────────────────────────────
  private askTransition(cfg: {
    title: string;
    message: string;
    confirmLabel: string;
    busyLabel: string;
    danger: boolean;
    run: () => Observable<unknown>;
    onSuccess?: (result: unknown) => void;
  }): void {
    this.txTitle.set(cfg.title);
    this.txMessage.set(cfg.message);
    this.txConfirmLabel.set(cfg.confirmLabel);
    this.txBusyLabel.set(cfg.busyLabel);
    this.txDanger.set(cfg.danger);
    this.txRun = cfg.run;
    this.txOnSuccess = cfg.onSuccess ?? null;
    this.txError.set(null);
    // Reset du preview — seul `askCancel` le peuple ensuite.
    this.previewLoading.set(false);
    this.preview.set(null);
    this.previewError.set(null);
    this.txOpen.set(true);
  }

  askAccept(): void {
    this.askTransition({
      title: 'rentals.tx.acceptTitle',
      message: 'rentals.tx.acceptMsg',
      confirmLabel: 'rentals.tx.accept',
      busyLabel: 'rentals.tx.accepting',
      danger: false,
      run: () => this.rentalsService.changeStatusMine(this.id, 'CONFIRMED'),
    });
  }

  askReject(): void {
    this.askTransition({
      title: 'rentals.tx.rejectTitle',
      message: 'rentals.tx.rejectMsg',
      confirmLabel: 'rentals.tx.reject',
      busyLabel: 'rentals.tx.rejecting',
      danger: true,
      run: () => this.rentalsService.changeStatusMine(this.id, 'CANCELLED'),
    });
  }

  askStart(): void {
    this.askTransition({
      title: 'rentals.tx.startTitle',
      message: 'rentals.tx.startMsg',
      confirmLabel: 'rentals.tx.start',
      busyLabel: 'rentals.tx.starting',
      danger: false,
      run: () => this.rentalsService.start(this.id),
    });
  }

  askFinish(): void {
    this.askTransition({
      title: 'rentals.tx.finishTitle',
      message: 'rentals.tx.finishMsg',
      confirmLabel: 'rentals.tx.finish',
      busyLabel: 'rentals.tx.finishing',
      danger: false,
      run: () => this.rentalsService.finish(this.id),
    });
  }

  askCancel(): void {
    this.askTransition({
      title: 'rentals.tx.cancelTitle',
      message: 'rentals.tx.cancelMsg',
      confirmLabel: 'rentals.tx.cancel',
      busyLabel: 'rentals.tx.cancelling',
      danger: true,
      run: () => this.rentalsService.cancel(this.id),
      // Le back renvoie le détail de remboursement → on l'affiche.
      onSuccess: (res) => {
        this.refundResult.set((res as CancellationRefund) ?? null);
        this.refundOpen.set(true);
      },
    });
    // Preview chiffrée (sans effet de bord) affichée dans le modal de confirmation.
    this.loadCancelPreview();
  }

  /** Charge le remboursement projeté à afficher dans le modal de confirmation. */
  private loadCancelPreview(): void {
    this.previewLoading.set(true);
    this.preview.set(null);
    this.previewError.set(null);
    this.rentalsService
      .cancellationPreview(this.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (p) => {
          this.preview.set(p);
          this.previewLoading.set(false);
        },
        error: () => {
          // Best-effort : le preview reste optionnel, l'annulation reste possible.
          this.previewLoading.set(false);
          this.previewError.set('Estimation du remboursement indisponible.');
        },
      });
  }

  closeRefund(): void { this.refundOpen.set(false); }

  closeTx(): void { if (this.txBusy()) return; this.txOpen.set(false); }

  confirmTx(): void {
    if (!this.txRun || this.txBusy()) return;
    this.txBusy.set(true);
    this.txError.set(null);
    this.txRun()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.txBusy.set(false);
          this.txOpen.set(false);
          this.txOnSuccess?.(res);
          this.fetch();
        },
        error: (err: unknown) => {
          this.txBusy.set(false);
          this.txError.set(
            toErrorMessage(err, 'errors.actionFailed', {
              403: "Vous n'êtes pas autorisé à effectuer cette action sur cette location.",
            }),
          );
        },
      });
  }

  // ── Confirmation espèces ───────────────────────────────────────────────
  openCash(): void {
    this.cashRef.setValue('');
    this.cashError.set(null);
    this.cashOpen.set(true);
  }
  closeCash(): void { if (this.cashBusy()) return; this.cashOpen.set(false); }

  confirmCash(): void {
    const invId = this.invoiceId();
    if (invId == null || this.cashBusy()) return;
    this.cashBusy.set(true);
    this.cashError.set(null);
    this.invoicesService
      .confirmCash(invId, this.cashRef.value.trim() || undefined)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.cashBusy.set(false);
          this.cashOpen.set(false);
          this.fetch();
        },
        error: (err: unknown) => {
          this.cashBusy.set(false);
          this.cashError.set(
            toErrorMessage(err, 'errors.confirmFailed', {
              403: "Seul le propriétaire du véhicule peut confirmer l'encaissement.",
            }),
          );
        },
      });
  }

  // delete (self-service)
  openDelete(): void { this.deleteError.set(null); this.deleteOpen.set(true); }
  closeDelete(): void { if (this.deleting()) return; this.deleteOpen.set(false); }
  confirmDelete(): void {
    this.deleting.set(true);
    this.deleteError.set(null);
    this.rentalsService.deleteMine(this.id).subscribe({
      next: () => {
        this.deleting.set(false);
        this.deleteOpen.set(false);
        void this.router.navigate(['/my-rentals']);
      },
      error: (err: unknown) => {
        this.deleting.set(false);
        this.deleteError.set(toErrorMessage(err, 'errors.deleteFailed'));
      },
    });
  }
}
